"use client";

import { ReactNode, useContext, useState } from "react";
// eslint-disable-next-line import/named
import { useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { createFormFields } from "./model";
import { SignOnIcon } from "@/svg/education/sign-on-icon";
import { trans } from "@/service/i18n/i18n";
import { preSubmit } from "@/forms/validation";
import { clientPatch } from "@/service/client-api/client-api";
import { UserContext } from "@/contexts/user-context";
import { useRouter } from "@bprogress/next";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { CheckboxField } from "@/forms/fields/checkbox-field/checkbox-field";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { subscribeToCommunicationCenter } from "@/components/common/subscribe-to-communication-center/service";

export function SubscribeToCommunicationCenter({
  state: { heading, paragraph, apiSubscribe },
  Action = (
    <>
      <SignOnIcon />
      {trans("subscription_list.form.confirm")}
    </>
  ),
  subscriptionInfo,
  onSuccess,
  close,
  isOpen,
}: {
  state: SubscriptionListState;
  ActionSubscribed?: ReactNode;
  Action?: ReactNode;
  subscriptionInfo?: string;
  onSuccess?: () => void;
  close: () => void;
  isOpen: boolean;
}) {
  const router = useRouter();
  const { defaultNewsletter } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [fields, setFields] = useImmer<ReturnType<typeof createFormFields>>(
    createFormFields(defaultNewsletter),
  );

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;
  if (!onSuccess) {
    onSuccess = function () {
      router.refresh();
    };
  }

  const onSubmit = async (submitData: Record<keyof typeof fields, any>) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    if (submitData.newsletter === true) {
      await clientPatch("profile.update", {}, { newsletter: true });
    }

    await subscribeToCommunicationCenter(
      setLoading,
      setError,
      onSuccess,
      apiSubscribe,
      close,
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => close()}>
      <DialogContent
        className="flex w-full max-w-[80vw] items-center rounded-xl md:max-w-[512px]"
        innerClassName={`p-4 md:p-6`}
      >
        <Form {...form}>
          <form
            className={"flex flex-col items-center justify-center"}
            onSubmit={handleSubmit(onSubmit)}
          >
            <h2 className={`text-foreground mb-4 text-lg font-semibold`}>
              {heading}
            </h2>
            <p className={"text-center text-sm text-zinc-600"}>{paragraph}</p>

            <div className={"mt-6 mb-8 flex w-full flex-col gap-1.5"}>
              {fields.newsletter && (
                <CheckboxField
                  field={fields.newsletter}
                  wrapperClassName="my-0"
                />
              )}

              {subscriptionInfo && (
                <p className={`text-muted-foreground pl-6 text-sm font-normal`}>
                  {subscriptionInfo}
                </p>
              )}
            </div>

            <Button variant={"blue"} type="submit" className="w-full">
              {loading && <LoaderIcon className="animate-spin" />}
              {Action}
            </Button>

            <FormError error={error} />
          </form>
        </Form>
        <DialogClose />
      </DialogContent>
    </Dialog>
  );
}
